import cn from 'classnames';
import NextImage, { ImageProps as NextImageProps, ImageLoader } from 'next/image';

import { useRouteContext } from 'src/hooks/useRouteContext';

import { ImageData } from 'src/types/components';

import styles from './Image.module.scss';

export type ImageProps = NextImageProps & {
  className?: string;
};

type URL = {
  url: string;
  width: number;
};

type URLSrcSetMap = Record<string, URL>;

const getSrcSet = ({ src, responsiveSizes }: ImageData): URLSrcSetMap | null => {
  if (typeof responsiveSizes !== 'object') {
    return null;
  }

  // Parse image width from url
  const getWidth = (url: string) => {
    const match = url.match(/-\d+x\d+\./);
    const size = Array.isArray(match) ? match[0] : '';
    const endIndex = size.indexOf('x');
    return size ? parseInt(size.slice(1, endIndex)) : 0;
  };

  // Get responsive urls and sort the urls by width (ascending)
  const urls: URL[] = Object.values(responsiveSizes)
    .filter((url) => typeof url === 'string' && url !== src)
    .map((url) => ({ url, width: getWidth(url) }))
    .filter((url) => url.width !== 0);

  urls.sort((first, second) => first.width - second.width);

  if (urls.length === 0) {
    return null;
  }

  return urls.reduce((map, url) => {
    const width = url.width ?? '_default';
    return {
      ...map,
      [width]: url,
    };
  }, {});
};

type ImageLoaderCreator = (srcSetMap: URLSrcSetMap) => ImageLoader;
const createImageLoader: ImageLoaderCreator = (srcSetMap) => {
  const imageLoader: ImageLoader = (props) => {
    const { src, width } = props;

    const defaultSrc = `${src}?width=${width}`;

    const targetWidthIndex =
      Object.keys(srcSetMap).findIndex((targetWidth) => parseInt(targetWidth) > width) - 1;

    if (targetWidthIndex >= 0) {
      const targetWidth = Object.keys(srcSetMap)[targetWidthIndex];

      const responsiveSize = targetWidth && srcSetMap[targetWidth];
      if (responsiveSize) {
        return `${responsiveSize.url}?width=${width}`;
      }
    }

    return defaultSrc;
  };

  return imageLoader;
};

const Image = (props: ImageData): JSX.Element => {
  const { routeData } = useRouteContext();
  const resize = routeData?.envVars.resizeImages;

  const srcSet = getSrcSet(props);

  const imageLoader = srcSet ? createImageLoader(srcSet) : undefined;

  const { responsiveSizes, ...imageProps } = props;

  return (
    <NextImage
      sizes="100vw"
      loader={imageLoader}
      unoptimized={resize !== 'true' || srcSet === null}
      {...imageProps}
      className={cn(props.className, styles.main)}
    />
  );
};
export default Image;
